import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter  } from 'react-router';
import { isEmpty } from 'lodash';
import DataGrid from '../components/datagrid/DataGrid';
import AlertDialog from '../components/utils/AlertDialog';
import {
  setEditList,
  deletePlacements,
  cancelPlacements
} from "../actions/placementActions";
import {
  updatePlacement,
  pushPlacementsAndUpdateOrder,
  clonePlacementsAndUpdateOrder
} from '../actions/orderActions';
import { exportPlacements } from './placement/importExport';
import { isLockedOrder } from '../utils/adManagerHelpers';
import { countLineItems } from '../utils/countLineItems';
import {
  GLO_PLACEMENT,
  GAM_LINE_PRIMARY,
  GAM_PLI_PRIMARY
} from '../utils/link';
import { TrackingCtx  } from "../tracking";
import { computeTotalContractAmount, quantityDerivedFromDates } from '../utils/totalContractAmount';

const pushStatusBadgeConf = {
  'COMPLETED': 'success',
  'NEEDS_UPDATE': 'warn',
  'UPDATED': 'success',
  'ERROR': 'error',
  'NOT AD MANAGER': 'warn'
};

const IO_STATUS_CAN_PUSH = 4;


export class PlacementsGrid extends Component {

  constructor(props) {
    super(props);
    this.toDelete = [];
    this.toCancel = [];
    this.state = {
      dialog: {
        open: false,
        title: '',
        message: '',
      }
    };
    this.columns = [
      { id: 'id', label: 'Galileo ID', link: GLO_PLACEMENT, filterable: true },
      { id: 'dfpLineItemId', label: 'AM Line Item ID', link: GAM_LINE_PRIMARY, visible: true, filterable: true },
      { id: 'dfpProposalLineItemId', label: 'AM PLI ID', link: GAM_PLI_PRIMARY, visible: true, filterable: true },
      { id: 'pushStatus', label: 'Push Status', badge: pushStatusBadgeConf, long: true, filterable: true },
      { id: 'dfpStatus', label: 'AM Status', filterable: true },
      { id: 'agencyPlacementId', label: 'Agency ID', filterable: true, editable: true },
      { id: 'agencyBillingId', label: 'Billing ID', filterable: true, editable: true },
      { id: 'agencyPackageName', label: 'Agency Package Name', filterable: true },
      { id: 'packageId', label: 'Package ID', visible: true, filterable: true },
      { id: 'agencyPlacementName', label: 'Agency Placement Name', editable: true, link: GLO_PLACEMENT, filterable: true, long: true },
      { id: 'strategy', label: 'Strategy', filterable: true, visible: false },
      { id: 'billingGroup', label: 'Billing Group', editable: true, filterable: true },
      { id: 'creativeProducts', label: 'Creative Products', filterable: true },
      { id: 'salesProduct', label: 'Sales Product', filterable: true, visible: false },
      { id: 'start', label: 'Start Date', date: 'MM/DD/YYYY', editable: true, filterable: true },
      { id: 'end', label: 'End Date', date: 'MM/DD/YYYY', editable: true, filterable: true },
      { id: 'contractedQuantity', label: 'Quantity', numeric: true, totals: true, editable: true, filterable: true },
      { id: 'ioRate', label: 'Rate', currency: 'USD', editable: true, filterable: true },
      { id: 'rateType', label: 'Rate Type' },
      { id: 'packageType', label: 'Package Type' },
      { id: 'totalContractAmount', label: 'Contract Amount', numeric: true, currency: 'USD', totals: true },
      { id: 'impressions', label: 'Delivered Impressions', numeric: true, totals: true },
      { id: 'clicks', label: 'Clicks', numeric: true, totals: true },
      { id: 'ctr', label: 'CTR', numeric: true, totals: true },
      { id: 'presetTargeting', label: 'Standard Targeting', filterable: true, editable: false },
      { id: 'customTargeting', label: 'Custom Targeting', filterable: true, editable: false }
    ];
    if (props.isReadOnly) {
      this.columns.forEach(c => c.editable = false);
    }
  }

  componentDidMount() {
    const { order } = this.props;
    const activeOrder = localStorage.getItem('activeOrder');
    if (order && parseInt(activeOrder, 10) !== order.id) {
      if (this.grid) {
        this.grid.clearFilter();
      }
      localStorage.setItem('activeOrder', order.id);
    }
  }

  getRows(context) {
    let { order, placements } = this.props;
    if (context === 'Package' && !isEmpty(order)) {
      placements = order.Placements;
    }
    if (isEmpty(placements)) {
      return [];
    } else {
      return placements.map(p => Object.assign({}, p, {
        creativeProducts: !isEmpty(p.CreativeProducts) ? p.CreativeProducts.map(c => c.name).join() : '',
        rateType: p.IoRateType ? p.IoRateType.name : '',
        strategy: !isEmpty(p.Strategy) ? p.Strategy.name : '',
        salesProduct: isEmpty(p.SalesProduct) ? '' : p.SalesProduct.name,
        dfpLineItemId: p.LineItems.length > 1 ? p.allAmIds : p.dfpLineItemId,
        pushStatus: this.handlePushStatus(p),
        presetTargeting: !isEmpty(p.CustomCriteriaPresets) ? p.CustomCriteriaPresets.map(cpr => cpr.name).join(', ') : '',
        customTargeting: p.customTargeting
      }));
    }
  }

  // Handle status on multiple line items by comparing status
  // and showing the "worst case" where error > needs_update > updated
  handlePushStatus(p) {
    if (!p.LineItems) return p.pushStatus;
    if (p.LineItems.length === 0) return p.pushStatus;
    let pushError = '';
    let pushStatus = '';
    p.LineItems.forEach (li => {
      if (li.pushError) pushError = li.pushError.slice(0, 100);
      if (li.pushStatus && pushStatus !== 'NEEDS_UPDATE') pushStatus = li.pushStatus;
    });
    return isEmpty(pushError) ? pushStatus : pushError;
  }

  onAdd = () => {
    const { order, history, context, pkgId } = this.props;
    const { itemRemainder } = countLineItems(order);
    if ((order && !order.Placements) || itemRemainder > 0) {
      if (context === "Package") {
        if (pkgId) {
          history.push(`/ios/${order.id}/placement/create?pkg=${pkgId}`);
        }
      } else {
        history.push(`/ios/${order.id}/placement/create`);
      }
    }
  };

  handlePushSettings = (pushSettings) => {
    const { dispatch, order, onNotifyUpload, history } = this.props;
    const { selected } = this.state;
    const ids = selected.map(p => p.id);
    const pushRules = this.getPushRules(pushSettings);
    dispatch(pushPlacementsAndUpdateOrder(order.id, ids, pushRules, history));
    onNotifyUpload (ids);
  };

  getPushRules = (pushSettings) => {
    const pushRules = [];
    if (pushSettings) {
      pushRules.push(pushSettings);
    }
    if (localStorage.getItem('soapLog')) {
      pushRules.push('soapLog')
    }
    if (localStorage.getItem('gamDirect')) {
      pushRules.push('gamDirect')
    }
    if (localStorage.getItem('forceStart')) {
      pushRules.push('forceStart')
    }
    return pushRules;
  };

  onEdit = selected => {
    const { dispatch, history, order, context, pkgId } = this.props;
    const ids = selected.map(p => p.id);
    const [first ] = ids;
    dispatch(setEditList(ids));
    if (context === "Package") {
      history.push(`/ios/${order.id}/placement/${first}?pkg=${pkgId}`);
    } else {
      history.push(`/ios/${order.id}/placement/${first}`);
    }
  };

  onLoad = placements => {
    this.setBatch(placements);
  };

  onSort = placements => {
    this.setBatch(placements);
  };

  onFilter = placements => {
    this.setBatch(placements);
  };

  setBatch = placements => {
    const { dispatch } = this.props;
    const ids = placements.map(p => p.id);
    dispatch(setEditList(ids))
  };

  // We should not delete placements that have Ad Manager IDs
  onDelete = selected => {
    this.toDelete = selected.filter(p =>
      !p.dfpProposalLineItemId &&
      isEmpty(p.LineItems.map(li => li.adManagerLineitemId).filter(Boolean))
    ).map(p => p.id);
    const dialog = {};
    dialog.open = true;
    dialog.title = 'Delete placements';
    dialog.message = 'None of the selected placements can be deleted';
    dialog.onCancel = this.closeDialog;
    dialog.cancelTitle = 'Do not delete';
    dialog.okTitle = ' ';
    if (this.toDelete.length > 0) {
      dialog.title = `${dialog.title} (${this.toDelete.length})`;
      if (this.toDelete.length > 6) {
        dialog.message = `Are you sure you want to delete ${this.toDelete.length} placements?`;
      } else {
        dialog.message = `Are you sure you want to delete placements: ${this.toDelete.join(',')}?`;
      }
      dialog.onOk = this.confirmDelete;
      dialog.okTitle = 'Delete';
      dialog.okColor = "secondary";
      dialog.cancelTitle = 'Do not delete';
    }
    this.setState({ dialog });
  };

  confirmDelete = () => {
    const { dispatch } = this.props;
    this.closeDialog();
    dispatch(deletePlacements(this.toDelete));
  };

  // We should only cancel placements that have Ad Manager IDs and have not been canceled.
  onCancel = selected => {
    const notCanceled = selected.filter(p => p.agencyPlacementId !== 'CANCEL');
    this.toCancel = notCanceled.filter(p =>
      !isEmpty(p.LineItems.map(li => li.adManagerLineitemId).filter(Boolean))
    ).map(p => p.id);
    const dialog = {};
    dialog.open = true;
    dialog.title = 'Cancel placements';
    dialog.message = 'None of the selected placements can be canceled';
    dialog.onCancel = this.closeDialog;
    dialog.cancelTitle = 'Do not cancel';
    dialog.okTitle = ' ';
    if (this.toCancel.length > 0) {
      dialog.title = `${dialog.title} (${this.toCancel.length})`;
      dialog.message = `Are you sure you want to cancel placements: ${this.toCancel.join(',')}`;
      dialog.onOk = this.confirmCancel;
      dialog.cancelTitle = 'Do not cancel';
      dialog.okTitle = 'Cancel Placements';
      dialog.okColor = "secondary";
    } else {
      dialog.title = 'Cancel placements';
      dialog.message = 'None of the selected placements can be canceled';
      dialog.onCancel = this.closeDialog;
      dialog.cancelTitle = 'Do not cancel';
      dialog.okTitle = ' ';
    }
    this.setState({ dialog });
  };

  confirmCancel = () => {
    const { dispatch } = this.props;
    this.closeDialog();
    dispatch(cancelPlacements(this.toCancel));
  };

  closeDialog = () => {
    this.setState({
      dialog: {
        open: false,
        title: '',
        message: '',
        onCancel: null,
        onOk: null,
        cancelTitle: '',
        okTitle: '',
        okColor: ''
      }
    })
  };

  onClone = (selected, numOfCopies) => {
    const { dispatch, order } = this.props;
    const numCopies = this.keepCloneWithinLimit(order, selected.length, numOfCopies);
    if (numCopies) {
      dispatch(clonePlacementsAndUpdateOrder(order, selected, numOfCopies));
    }
  };

  keepCloneWithinLimit = (order, numSelected, numCopies) => {
    const { itemCount, itemLimit } = countLineItems(order);
    const numberAfterCloning = itemCount + numSelected * numCopies;
    if (numberAfterCloning <= itemLimit) {
      return numCopies;
    }
    if (numCopies === 1) {
      return false;
    }
    while (numCopies > 1 && numberAfterCloning > itemLimit) {
      numCopies -= 1;
    }
    if (numberAfterCloning <= itemLimit) {
      return numCopies;
    }
    return 0;
  };

  onCellEdit = (placement, colId, value, done) => {
    const { dispatch } = this.props;
    placement[colId] = value;
    if (['contractedQuantity', 'ioRate', 'start', 'end'].includes(colId)) {
      const ret = computeTotalContractAmount(
        placement.contractedQuantity,
        placement.ioRate,
        placement.IoRateTypeId,
        placement.start,
        placement.end,
        placement.lineItemType
      );
      placement.totalContractAmount = ret.total;
      // Only overwrite qty when the rate type derives it from dates (CPD / Flat Fee AM).
      // For CPM, keep the edited impressions quantity.
      if (quantityDerivedFromDates(placement.IoRateTypeId, placement.lineItemType)) {
        placement.contractedQuantity = ret.quantity;
      }
    }
    placement.legend = 'react grid';
    dispatch(updatePlacement(placement, done));
  };

  exportSelected = selected => {
    const { order } = this.props;
    exportPlacements(order, selected);
  };

  exportAll = (placements) => {
    const { order } = this.props;
    exportPlacements(order, placements);
  };

  customizeColumns = columns => {
    const { order } = this.props;
    columns.dfpLineItemId.link = GAM_LINE_PRIMARY;
    columns.dfpLineItemId.visible = false;
    columns.dfpProposalLineItemId.visible = false;
    if (order && order.dfpProposalId != null) {
      columns.dfpLineItemId.visible = false;
      columns.dfpProposalLineItemId.visible = true;
    } else if (order && order.dfpOrderId != null){
      columns.dfpLineItemId.visible = true;
      columns.dfpProposalLineItemId.visible = false;
    }
    if (order && order.orderType === 'Programmatic') {
      columns.agencyPlacementId.visible = false;
      columns.agencyBillingId.visible = false;
      columns.billingGroup.visible = false;
    } else {
      columns.agencyPlacementId.visible = true;
      columns.agencyBillingId.visible = true;
      columns.billingGroup.visible = true;
    }
  };

  render() {
    const { order, expanded, onToggleExpand, isLocked, context, pkgId, amPushRules, isReadOnly, togglePushSettings, pushPlacementsFromOrder, setPushData, whitelist } = this.props;
    const { dialog } = this.state;
    const { itemRemainder, itemCount } = countLineItems(order);
    const lockedOrder = isLocked || isLockedOrder(order);

    return(
      <div>
        <AlertDialog {...dialog} />
        <TrackingCtx.Consumer>
        {tracking => (
          <TrackingCtx.Provider value={{...tracking, action:"Placements Grid"}}>
          <DataGrid
            innerRef={grid => this.grid = grid}
            minWidth={window.innerWidth - 40}
            columns={this.columns}
            onCustomizeColumns={this.customizeColumns}
            sortColumn='id'
            data={this.getRows(context)}
            title='Placements'
            sortable
            filterable
            showFilterRow
            autosaveFilters
            selectable
            pagination
            onLoad={this.onLoad}
            onFilter={this.onFilter}
            onSort={this.onSort}
            onAdd={itemRemainder > 0 && !lockedOrder && (context !== 'Package' || pkgId) ? this.onAdd : null}
            onEdit={this.onEdit}
            onUpload={(order && (order.IoStatusId >= IO_STATUS_CAN_PUSH || order.orderType === 'Programmatic')) ? this.onUpload : null}
            onClone={itemRemainder > 0 && !lockedOrder ? this.onClone : null}
            onDelete={!lockedOrder ? this.onDelete : null}
            onCancel={this.onCancel}
            onExport={this.exportAll}
            onSelectedExport={this.exportSelected}
            onCellEdit={this.onCellEdit}
            lineItemCount={itemCount}
            offsetTop={310}
            expanded={expanded}
            locked={lockedOrder}
            onToggleExpand={onToggleExpand}
            amPushRules={amPushRules}
            isReadOnly={isReadOnly}
            setPushData={setPushData}
            togglePushSettings={togglePushSettings}
            handlePushSettings={this.handlePushSettings}
            pushPlacementsFromOrder={pushPlacementsFromOrder}
            pushType='placements'
            whitelist={whitelist}
          />
          </TrackingCtx.Provider>
        )}
        </TrackingCtx.Consumer>
      </div>
    )
  }
}

PlacementsGrid.propTypes = {
  onNotifyUpload: PropTypes.func.isRequired,
  placements: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLocked: PropTypes.bool.isRequired
};

const mapStateToProps = state => {
  const { orderRequest } = state;
  return {
    placements: orderRequest.placements
  }
};

export default withRouter(connect(mapStateToProps)(PlacementsGrid));
